<template>
  <div class="container-fluid">
    <!-- Accounts Table -->
    <div class="mt-2 row" id="element-to-print">
      <div
        class="container-fluild table-main remove-styles2 remove-border responsiveness"
      >
        <!-- PDF Header (hidden for normal view) -->
        <div v-if="bookType == 'pdf'" class="row d-none">
          <table
            class="table remove-styles mt-0 col-md-12 table-hover table-header-area"
          >
            <thead>
              <tr class="font-weight-bold">
                <th colspan="2">
                  <div>
                    <div class="s-20 fw-500">Cash Flow Report</div>
                    <div class="s-18 font-weight-600">
                      {{ tenantDisplayName }}
                    </div>
                    <div class="s-18 fw-500">
                      <span class="mr-1">Date Range:</span>
                      <span
                        >{{ formatDate(startDate) }} to
                        {{ formatDate(endDate) }}</span
                      >
                    </div>
                  </div>
                </th>
                <th class="text-right text-dak s-14">
                  <div>
                    <img
                      :src="churchLogo"
                      v-if="churchLogo"
                      class="link-image mr-2"
                      crossorigin="anonymous"
                      alt=""
                    />
                    <img
                      src="../../../assets/dashboardlinks/churchcloud.png"
                      v-else
                      class="link-image mr-2"
                      alt=""
                    />
                  </div>
                </th>
              </tr>
            </thead>
          </table>
        </div>

        <!-- Main Report Table -->
        <table id="table" class="table remove-styles mt-0 table-hover table-header-area">
          <thead class="table-header-ar">
            <tr class="font-weight-bold">
              <th colspan="2">CASH INFLOW AND OUTFLOW</th>
              <th class="text-right text-dak s-14">
                {{ formatDate(startDate) }} <br />
                to {{ formatDate(endDate) }}
              </th>
            </tr>
          </thead>

          <!-- Opening Balance -->
          <!-- <tbody>
            <tr class="table-header-area-main">
              <td colspan="3" class="text-dak fw-400 s-14">
                Opening Balance: {{ currentUser.currencySymbol }}
                {{ formatAmount(openingBalance) }}
              </td>
            </tr>
          </tbody> -->

          <!-- Dynamic Sections -->
          <tbody
            v-for="(category, categoryName) in groupedCashFlow"
            :key="categoryName"
          >
            <!-- Category Header -->
            <tr class="table-header-area-main">
              <td colspan="3" class="fw-400 s-14">
                {{ formatCategoryName(categoryName) }}
              </td>
            </tr>

            <!-- Inflows -->
            <tr>
              <td class="text-blue-400 fw-500 s-14">Inflow</td>
              <td></td>
              <td class="text-right">
                {{ currentUser.currencySymbol }}
                {{ formatAmount(category.totalInflows) }}
              </td>
            </tr>

            <!-- Outflows -->
            <tr>
              <td class="text-blue-400 fw-500 s-14">Outflow</td>
              <td></td>
              <td class="text-right">
                {{ currentUser.currencySymbol }}
                {{ formatAmount(category.totalOutflows) }}
              </td>
            </tr>

            <!-- Net Balance -->
            <tr class="font-weight-bold">
              <td class="text-dak">Total Balance</td>
              <td></td>
              <td class="text-right">
                {{ currentUser.currencySymbol }}
                {{
                  formatAmount(category.totalInflows - category.totalOutflows)
                }}
              </td>
            </tr>
          </tbody>

          <!-- Grand Totals -->
          <!-- <tr class="table-header-area-main">
            <td class="font-weight-bold">TOTAL INFLOW</td>
            <td></td>
            <td class="text-right font-weight-bold">
              {{ currentUser.currencySymbol }}
              {{ formatAmount(totalInflows) }}
            </td>
          </tr>

          <tr class="table-header-area-main">
            <td class="font-weight-bold">TOTAL OUTFLOW</td>
            <td></td>
            <td class="text-right font-weight-bold">
              {{ currentUser.currencySymbol }}
              {{ formatAmount(totalOutflows) }}
            </td>
          </tr> -->

          <!-- Overall Net Cash Flow -->
          <tr class="table-header-area-main">
            <td class="font-weight-bold">Net Balance</td>
            <td></td>
            <td class="text-right font-weight-bold">
              {{ currentUser.currencySymbol }}
              {{ formatAmount(netCashFlow) }}
            </td>
          </tr>

          <!-- Closing Balance -->
          <!-- <tr class="table-header-area-main">
            <td colspan="3" class="text-dak fw-400 s-14">
              Closing Balance: {{ currentUser.currencySymbol }}
              {{ formatAmount(closingBalance) }}
            </td>
          </tr> -->
        </table>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, watchEffect, onMounted, ref, watch, nextTick } from "vue";
import { useStore } from "vuex";
import axios from "@/gateway/backendapi";
import exportService from "../../../services/exportFile/exportserjs";
import dateFormatter from "../../../services/dates/dateformatter";

const props = defineProps({
  startDate: {
    type: String,
    default: "",
  },
  endDate: {
    type: String,
    default: "",
  },
  fileHeaderToExport: {
    type: Array,
    required: true,
  },
  fileToExport: {
    type: Array,
    required: true,
  },
  fileName: {
    type: String,
    default: "",
  },
  bookType: {
    type: String,
    default: "",
  },
  cashFlowSummaryData: {
    type: Object,
    default: () => ({}),
  },
});

const tenantData = ref({});
const churchLogo = ref("");
const store = useStore();

const openingBalance = ref(0);

const cashFlowData = computed(() => props.cashFlowSummaryData || {});

// Group cash flow data by category with inflows and outflows
const groupedCashFlow = computed(() => {
  const groups = {};

  // Process cash flow data to group by category
  if (cashFlowData.value.cashFlow) {
    cashFlowData.value.cashFlow.forEach((item) => {
      const category = item.category || "Other";

      if (!groups[category]) {
        groups[category] = {
          totalInflows: 0,
          totalOutflows: 0,
          netCashFlow: 0,
        };
      }

      groups[category].totalInflows += item.inflows || 0;
      groups[category].totalOutflows += Math.abs(item.outflows) || 0;
      groups[category].netCashFlow =
        groups[category].totalInflows - groups[category].totalOutflows;
    });
  }

  return groups;
});

// Calculate overall totals
const totalInflows = computed(() => {
  return Object.values(groupedCashFlow.value).reduce(
    (sum, category) => sum + category.totalInflows,
    0
  );
});

const totalOutflows = computed(() => {
  return Object.values(groupedCashFlow.value).reduce(
    (sum, category) => sum + category.totalOutflows,
    0
  );
});

const netCashFlow = computed(() => {
  return totalInflows.value - totalOutflows.value;
});

const closingBalance = computed(() => {
  return openingBalance.value + netCashFlow.value;
});

// Helper function to format category names
const formatCategoryName = (name) => {
  return name
    .replace(/([A-Z])/g, " $1")
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
};

// Watch for changes in cashFlowData to update opening balance
watchEffect(() => {
  if (cashFlowData.value.openingBalance !== undefined) {
    openingBalance.value = cashFlowData.value.openingBalance;
  }
});

// ... (keep all other existing methods and computed properties) ...

const emit = defineEmits(["download-complete"]); // Add this line

const imageConverter = ref("");
const preloadChurchLogo = async () => {
  try {
    console.log("Original URL:", churchLogo.value); // Verify the URL is correct

    const response = await fetch(churchLogo.value, {
      mode: "cors",
      credentials: "include",
    });

    const blob = await response.blob();

    const objectUrl = URL.createObjectURL(blob);
    console.log("Object URL created:", objectUrl);

    imageConverter.value = objectUrl;
  } catch (error) {
    imageConverter.value = churchLogo.value; // Fallback to original URL
  }
};

preloadChurchLogo();

// Watch for changes in bookType prop
watch(
  () => props.bookType,
  async (newVal, oldVal) => {
    if (!newVal || newVal === oldVal) return;

    try {
      if (newVal === "pdf") await nextTick();

      exportService.downLoadExcel(
        newVal,
        document.getElementById("element-to-print"),
        props.fileName,
        props.fileHeaderToExport,
        props.fileToExport,
        churchLogo.value
      );

      emit("download-complete", true);
    } catch (error) {
      emit("download-complete", false);
    }
  },
  { immediate: true } // Changed to true to catch all changes
);

const getChurchProfile = async () => {
  try {
    let { data } = await axios.get(
      `/GetChurchProfileById?tenantId=${getUser.value.tenantId}`
    );
    console.log(data);
    churchLogo.value = data.returnObject.logo;
  } catch (err) {
    console.error(err);
  }
};

const tenantDisplayName = computed(() => {
  if (!tenantData.value.churchName) return "";
  const name =
    tenantData.value.churchName.length < 20
      ? tenantData.value.churchName
      : `${tenantData.value.churchName.slice(0, 20)}...`;
  return name;
});
const getUser = computed(() => {
  if (
    !store.getters.currentUser ||
    (store.getters.currentUser &&
      Object.keys(store.getters.currentUser).length == 0)
  )
    return "";
  return store.getters.currentUser;
});

watchEffect(() => {
  if (getUser.value) {
    tenantData.value = getUser.value;
    getChurchProfile();
  }
});
const currentUser = computed(() => {
  if (store && Object.keys(store.getters.currentUser).length > 0)
    return store.getters.currentUser;
  return "";
});

const formatDate = (date) => {
  return dateFormatter.monthDayYear(date);
};

// Format Amount
const formatAmount = (amount) =>
  amount.toLocaleString("en-NG", { minimumFractionDigits: 2 });
</script>

<style scoped>
.table th,
.table td {
  border-top: none;
}
.active {
  background: #ffffff;
  color: #08162e;
  font-weight: 700;
  width: 81px;
  height: 37px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  /* padding: 15px 0; */
}
.bg-secondary {
  background-color: #f8f9fa !important;
}
button.el-button {
  height: 36px !important;
  width: 139px !important;
  padding: 13px 24px !important;
  font-size: 13px !important;
}
.table-main {
  width: 100% !important;
  box-shadow: 0 0.063rem 0.25rem #02172e45 !important;
  /* border: 0.063rem solid #dde2e6 !important; */
  /* border-radius: 30px !important; */
  text-align: left !important;
  margin-bottom: auto !important;
  padding-bottom: 0.5rem !important;
}

.remove-styles {
  border: none !important;
  box-shadow: none !important;
  border-bottom: 0 !important;
  border-bottom-left-radius: 0 !important;
  border-bottom-right-radius: 0 !important;
}
.link-image {
  width: 44.88px;
  height: 43.52px;
  padding-right: 0;
  border-radius: 8px;
  object-fit: cover;
}

.remove-styles2 {
  padding-right: 0;
  padding-left: 0;
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}
table tbody tr,
table thead tr {
  border-bottom: 1px solid rgba(0, 0, 1, 0.2);
}
table tbody tr:first-child {
  border-bottom: none !important;
}
table tbody tr:last-child {
  border-bottom: none !important;
}

.remove-border {
  box-shadow: none !important;
}
.table {
  width: 100% !important;
  box-shadow: none !important;
  border: none !important;
  text-align: left;
  margin-bottom: auto !important;
  padding-bottom: 0.5rem;
}

.table thead th {
  font-weight: 700 !important;
  color: #000000 !important;
  font-size: 14px !important;
}
.table tbody tr {
  font-weight: 400 !important;
  color: #000000 !important;
  font-size: 14px !important;
}

.table-header-area-main {
  background-color: #d4dde4;
  /* background-color: #f4f4f4; */
}

.table-header-area {
  border-top-left-radius: 0;
  border-top-right-radius: 0;
}
.pdf-only {
  display: none;
}

/* Print/PDF specific CSS */
@media print {
  .pdf-only {
    display: block;
  }

  .no-print {
    display: none;
  }
}
</style>
